// Safe TSL Block interpreter — a deterministic, causal tree-walk evaluator. NO eval / new Function. Each
// stateful node (indicator / prev / cross) keeps rolling state in a WeakMap keyed by the node reference;
// per-bar memoization guarantees every stateful node advances exactly once per bar (so cond/logic
// evaluate both branches without corrupting buffers). The engine runs ast-backed user blocks through this
// — never through host-language code. It also adapts to the BlockGateRuntime contract so the existing
// determinism / no-lookahead / sizing / risk gates validate TSL blocks with no isolate.

import type { Expr, TslBlock } from './ast';
import { checkTslBlockShape, TSL_STRUCT_LIMITS } from './astGuard';
import type { BlockGateRuntime, GateCandle, PerBarOutput, RunError } from '../blockGate/runtime';

// Hard ceiling on any rolling window the interpreter will materialize (indicator periods, change/roc
// shifts, htf sub-periods). The publish budget (TSL_LIMITS.maxLookback = 500) is far below this, so no
// gate-passing block is affected; it exists so a hand-crafted AST/params combo (window = 1e9, or a NaN
// period that would defeat the `> period` trim) cannot grow per-node buffers past a fixed bound.
const HARD_WINDOW = TSL_STRUCT_LIMITS.maxWindowHard;
// Missing history is unknown, never a true trading signal. Keep unknown values
// through boolean expressions so `not (warmup comparison)` cannot enable entry.
const truth = (v: number): boolean => Number.isFinite(v) && v !== 0;

type Candle = GateCandle;
const srcVal = (s: string, c: Candle): number => {
  switch (s) {
    case 'open': return c.open; case 'high': return c.high; case 'low': return c.low; case 'close': return c.close; case 'volume': return c.volume;
    case 'hl2': return (c.high + c.low) / 2; case 'hlc3': return (c.high + c.low + c.close) / 3; case 'ohlc4': return (c.open + c.high + c.low + c.close) / 4;
    default: return NaN;
  }
};

// ── stateful node evaluators (causal) ─────────────────────────────────────────
function makeIndicator(name: string) {
  const buf: number[] = []; let prev = NaN; let prevClose = NaN;
  return {
    update(_name2: string, args: number[], c: Candle): number {
      const src = args[0]; const period = Math.max(1, Math.round(args[1] ?? args[0] ?? 1));
      switch (name) {
        // NaN-resilient (matches the Pine bodies): recompute over the window and return NaN if ANY element
        // is non-finite. Lets sma/stdev sit on top of warm-up-NaN series (indicator-on-indicator, StochRSI)
        // without the rolling sum poisoning permanently. Same left-to-right summation as the JS bodies.
        case 'sma': { buf.push(src); if (buf.length > period || buf.length > HARD_WINDOW) buf.shift(); if (buf.length < period) return NaN; let s = 0; for (const x of buf) { if (!Number.isFinite(x)) return NaN; s += x; } return s / period; }
        case 'ema': { const k = 2 / (period + 1); prev = Number.isFinite(prev) ? src * k + prev * (1 - k) : src; return prev; }
        case 'rma': { prev = Number.isFinite(prev) ? (prev * (period - 1) + src) / period : src; return prev; }
        case 'wma': { buf.push(src); if (buf.length > period || buf.length > HARD_WINDOW) buf.shift(); if (buf.length < period) return NaN; let num = 0, den = 0; for (let i = 0; i < buf.length; i++) { num += buf[i] * (i + 1); den += i + 1; } return num / den; }
        case 'stdev': { buf.push(src); if (buf.length > period || buf.length > HARD_WINDOW) buf.shift(); if (buf.length < period) return NaN; let s = 0; for (const x of buf) { if (!Number.isFinite(x)) return NaN; s += x; } const m = s / period; let v = 0; for (const x of buf) v += (x - m) * (x - m); return Math.sqrt(v / period); }
        case 'highest': { buf.push(src); if (buf.length > period || buf.length > HARD_WINDOW) buf.shift(); return buf.length < period ? NaN : Math.max(...buf); }
        case 'lowest': { buf.push(src); if (buf.length > period || buf.length > HARD_WINDOW) buf.shift(); return buf.length < period ? NaN : Math.min(...buf); }
        case 'change': { buf.push(src); const k = Math.round(args[1] ?? 1); if (buf.length > k + 1 || buf.length > HARD_WINDOW) buf.shift(); return buf.length > k ? src - buf[buf.length - 1 - k] : NaN; }
        case 'roc': { buf.push(src); const k = Math.round(args[1] ?? 1); if (buf.length > k + 1 || buf.length > HARD_WINDOW) buf.shift(); const past = buf.length > k ? buf[buf.length - 1 - k] : NaN; return past ? (src / past - 1) * 100 : NaN; }
        case 'rsi': {
          // TradingView ta.rsi convention (SMA-seeded Wilder, matching the Pine importer body): bar 0 has
          // no change; accumulate the first `period` changes, take their average as the seed at bar
          // `period` (first non-NaN), then Wilder-smooth. NaN before bar `period`.
          const self = this as any;
          // A nested indicator may not have a finite source during warmup (or
          // after a gap). Restart the seed; never add NaN to Wilder's accumulators.
          // Require a fresh finite source plus `period` consecutive finite changes.
          if (!Number.isFinite(src)) {
            prevClose = NaN; self._i = 0; self._sg = 0; self._sl = 0;
            self._ag = NaN; self._al = NaN; return NaN;
          }
          const i = self._i ?? 0;                          // current bar index (0-based)
          if (i === 0) { prevClose = src; self._i = 1; return NaN; }
          const ch = src - prevClose; prevClose = src;
          const g = Math.max(0, ch), l = Math.max(0, -ch);
          if (i <= period) {
            self._sg = (self._sg ?? 0) + g; self._sl = (self._sl ?? 0) + l;
            if (i === period) { self._ag = self._sg / period; self._al = self._sl / period; self._i = i + 1; return self._al === 0 ? 100 : 100 - 100 / (1 + self._ag / self._al); }
            self._i = i + 1; return NaN;
          }
          self._ag = (self._ag * (period - 1) + g) / period; self._al = (self._al * (period - 1) + l) / period;
          self._i = i + 1; return self._al === 0 ? 100 : 100 - 100 / (1 + self._ag / self._al);
        }
        case 'tr': { const tr = Number.isFinite(prevClose) ? Math.max(c.high - c.low, Math.abs(c.high - prevClose), Math.abs(c.low - prevClose)) : c.high - c.low; prevClose = c.close; return tr; }
        case 'atr': {
          // TradingView ta.atr = ta.rma(tr, len): SMA-seeded Wilder. NaN before bar `period`-1; seed =
          // mean of the first `period` true ranges; then Wilder. Matches the Pine importer's ATR body
          // (used by Supertrend) — the old RMA-from-first-value seed emitted from bar 0 and never matched.
          const self = this as any;
          const trv = Number.isFinite(prevClose) ? Math.max(c.high - c.low, Math.abs(c.high - prevClose), Math.abs(c.low - prevClose)) : c.high - c.low;
          prevClose = c.close;
          const i = self._ai ?? 0;
          if (i < period - 1) { self._asum = (self._asum ?? 0) + trv; self._ai = i + 1; return NaN; }
          if (i === period - 1) { self._asum = (self._asum ?? 0) + trv; prev = self._asum / period; self._ai = i + 1; return prev; }
          prev = (prev * (period - 1) + trv) / period; self._ai = i + 1; return prev;
        }
        // mulberry32: deterministic seeded PRNG. `s` advances by a fixed constant each bar; the value is a
        // bit-mix of s. Bit-exact with the legacy benchmark RNG body. State (`_rs`) carries across bars.
        case 'rng': { const self = this as any; let s = self._rs; if (s === undefined) s = (Number(src) || 1) >>> 0; s |= 0; s = (s + 0x6D2B79F5) | 0; self._rs = s >>> 0; let t = Math.imul(s ^ (s >>> 15), 1 | s); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }
        // Same-symbol HTF resample. args = [htfMinutes, period, kindCode]. Buckets close by timestamp; on
        // each bucket CLOSE feeds the final bucket-close into a streaming sub-indicator (sma/ema/dema/tema)
        // and returns the last-closed value — never the forming bucket (lookahead_off). Bit-exact with the
        // Pine HTF body (which computes the sub-indicator over the closed-bucket close array).
        case 'htf': {
          const self = this as any;
          const htfMs = Math.max(1, Math.round(args[0] || 60)) * 60000;
          const p = Math.max(1, Math.min(HARD_WINDOW, Math.round(args[1] || 12)));
          const kind = Math.round(args[2] || 0);          // 0 sma · 1 ema · 2 dema · 3 tema
          const ts = Number.isFinite(c.timestamp) ? c.timestamp : 0;
          const key = Math.floor(ts / htfMs);
          if (self._hk === undefined) { self._hk = key; self._hpc = c.close; return NaN; }
          if (key !== self._hk) {                          // previous bucket (close = self._hpc) just closed
            const x = self._hpc, kk = 2 / (p + 1);
            self._e1 = Number.isFinite(self._e1) ? x * kk + self._e1 * (1 - kk) : x;
            self._e2 = Number.isFinite(self._e2) ? self._e1 * kk + self._e2 * (1 - kk) : self._e1;
            self._e3 = Number.isFinite(self._e3) ? self._e2 * kk + self._e3 * (1 - kk) : self._e2;
            if (!self._hbuf) self._hbuf = [];
            self._hbuf.push(x); if (self._hbuf.length > p) self._hbuf.shift();
            let smaVal = NaN; if (self._hbuf.length >= p) { let s2 = 0; for (const y of self._hbuf) s2 += y; smaVal = s2 / p; }
            self._hlast = kind === 0 ? smaVal : kind === 1 ? self._e1 : kind === 2 ? (2 * self._e1 - self._e2) : (3 * (self._e1 - self._e2) + self._e3);
            self._hk = key; self._hpc = c.close;
            return self._hlast;
          }
          self._hpc = c.close;
          return self._hlast === undefined ? NaN : self._hlast;
        }
        // htfema1(htfMinutes, period) — higher-timeframe EMA with Pine `ta.ema(close[1], n)` +
        // request.security(lookahead_off) semantics: timestamp-bucket the close stream, take each CLOSED
        // bucket's final close, run an SMA-SEEDED EMA (matches indicators.ema: NaN before `period`, seed =
        // SMA of the first `period`), and expose the value EXCLUDING the most-recently-closed bucket (the
        // close[1] shift → htfEma[closedIdx-1]). Bit-exact with the legacy "HTF EMA 200" body. Causal +
        // prefix-invariant. (A separate primitive from htf, which has no close[1] shift and a first-value
        // EMA seed; htf is unchanged for its existing users.)
        case 'htfema1': {
          const self = this as any;
          const htfMs = Math.max(1, Math.round(args[0] || 60)) * 60000;
          const p = Math.max(1, Math.min(HARD_WINDOW, Math.round(args[1] || 200)));
          const ts = Number.isFinite(c.timestamp) ? c.timestamp : 0;
          const key = Math.floor(ts / htfMs);
          if (self._hk === undefined) { self._hk = key; self._hpc = c.close; self._cnt = 0; self._seed = 0; self._ema = NaN; self._ret = NaN; return NaN; }
          if (key !== self._hk) {                          // bucket (close = self._hpc) just closed
            const x = self._hpc;
            self._ret = self._ema;                          // close[1] shift: expose the PRE-append EMA = htfEma[idx-1]
            if (self._cnt < p) { self._seed += x; self._cnt++; self._ema = self._cnt === p ? self._seed / p : NaN; }
            else { const k = 2 / (p + 1); self._ema = Number.isFinite(self._ema) ? x * k + self._ema * (1 - k) : x; }
            self._hk = key; self._hpc = c.close;
            return self._ret;
          }
          self._hpc = c.close;
          return self._ret === undefined ? NaN : self._ret;
        }
        default: return NaN;
      }
    },
  };
}

export interface TslEnv { candle?: Candle; ctx?: Record<string, number>; params: Record<string, unknown>; state: Record<string, number>; }

export function createInterpreter(block: TslBlock) {
  // FAIL-CLOSED structural guard. A tslAst may arrive here WITHOUT ever passing the text parser or the
  // semantic validator (hand-crafted JSON in an imported strategy / tampered share / DB row), so before
  // walking anything we run the iterative depth/unique-node/cycle/shape/reserved-key check. Callers all
  // wrap interpretation in try/catch and surface the message, so a malicious AST degrades to a clean
  // per-block error instead of a stack overflow / infinite walk.
  //
  // Execution budget (deterministic, no timeouts needed): with the guard passed, per-run work is bounded
  // by construction — the grammar has no loops/recursion/dynamic access; `ev` is memoized per node per
  // bar, so each bar evaluates ≤ TSL_STRUCT_LIMITS.maxNodes unique nodes (shared DAG nodes count once);
  // every stateful node's rolling buffer is trimmed to ≤ HARD_WINDOW elements (prev barsAgo is guard-
  // capped at the same bound); recursion depth in `ev` ≤ TSL_STRUCT_LIMITS.maxDepth. Total work is
  // therefore ≤ bars × maxNodes × HARD_WINDOW steps — finite and input-independent. Gate-passing blocks
  // sit far inside these ceilings (300 nodes / 500 lookback), so behavior for valid blocks is unchanged.
  const structErr = checkTslBlockShape(block);
  if (structErr) throw new Error(`invalid TSL block: ${structErr}`);
  // Each top-level run (series/qty/risk call) gets FRESH per-node buffers + memo, so repeated runs are
  // independent (the determinism gate reruns the same input and must get identical output).
  let nodeState = new WeakMap<Expr, any>();
  let memo = new WeakMap<Expr, number>();

  function ev(e: Expr, env: TslEnv): number {
    const cached = memo.get(e); if (cached !== undefined) return cached;
    let out: number;
    switch (e.t) {
      case 'num': out = e.v; break;
      case 'bool': out = e.v ? 1 : 0; break;
      case 'param': { const v = Object.prototype.hasOwnProperty.call(env.params, e.name) ? env.params[e.name] : block.params?.[e.name]?.default; out = typeof v === 'boolean' ? (v ? 1 : 0) : Number(v); break; }
      case 'src': out = env.candle ? srcVal(e.src, env.candle) : NaN; break;
      case 'ctx': out = Number(env.ctx?.[e.field] ?? NaN); break;
      case 'state': out = Number(env.state[e.name] ?? 0); break;
      case 'indicator': { let st = nodeState.get(e); if (!st) { st = makeIndicator(e.name); nodeState.set(e, st); } out = st.update(e.name, e.args.map((a) => ev(a, env)), env.candle ?? { open: 0, high: 0, low: 0, close: 0, volume: 0, timestamp: 0 }); break; }
      case 'prev': { let st = nodeState.get(e); if (!st) { st = { buf: [] as number[] }; nodeState.set(e, st); } const v = ev(e.expr, env); st.buf.push(v); if (st.buf.length > e.barsAgo + 1) st.buf.shift(); out = st.buf.length > e.barsAgo ? st.buf[st.buf.length - 1 - e.barsAgo] : NaN; break; }
      case 'unop': { const a = ev(e.a, env); out = e.op === 'neg' ? -a : e.op === 'abs' ? Math.abs(a) : e.op === 'sign' ? Math.sign(a) : e.op === 'nz' ? (Number.isFinite(a) ? a : 0) : (Number.isFinite(a) ? (a ? 0 : 1) : NaN); break; }
      case 'binop': { const a = ev(e.a, env), b = ev(e.b, env); out = e.op === '+' ? a + b : e.op === '-' ? a - b : e.op === '*' ? a * b : e.op === '/' ? (b === 0 ? NaN : a / b) : e.op === 'min' ? Math.min(a, b) : Math.max(a, b); break; }
      case 'cmp': { const a = ev(e.a, env), b = ev(e.b, env); out = !Number.isFinite(a) || !Number.isFinite(b) ? NaN : (e.op === '>' ? a > b : e.op === '<' ? a < b : e.op === '>=' ? a >= b : e.op === '<=' ? a <= b : e.op === '==' ? a === b : a !== b) ? 1 : 0; break; }
      case 'logic': { const a = ev(e.a, env), b = ev(e.b, env);
        // Both sides always advance their rolling state, even when one decides
        // the result (false AND unknown = false; true OR unknown = true).
        out = e.op === 'and' ? (a === 0 || b === 0 ? 0 : !Number.isFinite(a) || !Number.isFinite(b) ? NaN : 1)
          : (truth(a) || truth(b) ? 1 : !Number.isFinite(a) || !Number.isFinite(b) ? NaN : 0); break;
      }
      case 'cross': { let st = nodeState.get(e); if (!st) { st = { pa: NaN, pb: NaN }; nodeState.set(e, st); } const a = ev(e.a, env), b = ev(e.b, env); const over = Number.isFinite(st.pa) && (e.dir === 'over' ? (st.pa <= st.pb && a > b) : (st.pa >= st.pb && a < b)); st.pa = a; st.pb = b; out = over ? 1 : 0; break; }
      case 'cond': { const c = ev(e.cond, env); const t = ev(e.then, env), f = ev(e.else, env); out = !Number.isFinite(c) ? NaN : c !== 0 ? t : f; break; } // both branches evaluated (memo-safe)
      default: out = NaN;
    }
    memo.set(e, out); return out;
  }

  function commitState(env: TslEnv) {
    if (!block.state) return;
    // Null-prototype scratch map: state names are guard-checked (never __proto__/constructor/prototype),
    // and the copy target has no prototype machinery to hit — writes are always plain data properties.
    const next: Record<string, number> = Object.assign(Object.create(null), env.state);
    for (const [name, s] of Object.entries(block.state)) next[name] = ev(s.update, env);
    Object.assign(env.state, next);
  }

  return {
    /** Per-bar evaluation for indicators/filters → { value, signals }. Streams candles in order (causal). */
    series(candles: Candle[], params: Record<string, unknown>): PerBarOutput {
      nodeState = new WeakMap();                       // fresh per-run buffers
      const state: Record<string, number> = Object.create(null); if (block.state) for (const [n, s] of Object.entries(block.state)) state[n] = s.init;
      const value: number[] = []; const signals: boolean[] = [];
      for (const candle of candles) {
        memo = new WeakMap();
        const env: TslEnv = { candle, params, state };
        value.push(block.value ? ev(block.value, env) : NaN);
        signals.push(block.signal ? truth(ev(block.signal, env)) : false);
        commitState(env);
      }
      return { value, signals };
    },
    /** Sizing: qty from ctx (pure — no series/state). */
    qty(ctx: Record<string, number>, params: Record<string, unknown>): number {
      nodeState = new WeakMap(); memo = new WeakMap();
      return block.qty ? ev(block.qty, { ctx, params, state: {} }) : NaN;
    },
    /** Risk, streaming form: one bar per call with the CALLER's ctx (the engine's checkBar dispatch).
     *  Indicator/prev/cross node buffers persist across calls on this interpreter instance, so the caller
     *  must feed bars in order, once each, and use a fresh interpreter per run. `state` is the TSL block's
     *  declared state map, owned by the caller so engine-side run state stays serializable-adjacent. */
    riskStep(candle: Candle, ctx: Record<string, number>, params: Record<string, unknown>, state: Record<string, number>): { action: string } | null {
      memo = new WeakMap();
      const ACTIONS = [null, { action: 'block_entry' }, { action: 'flatten' }, { action: 'allow' }, { action: 'resize_qty' }] as const;
      const env: TslEnv = { candle, ctx, params, state };
      const code = block.risk ? Math.round(ev(block.risk.action, env)) : 0;
      commitState(env);
      return (ACTIONS[code] ?? null) as { action: string } | null;
    },
    /** Risk: per-bar action over a candle/ctx stream → actions[]. */
    risk(candles: Candle[], params: Record<string, unknown>): Array<{ action: string } | null> {
      nodeState = new WeakMap();                       // fresh per-run buffers
      const state: Record<string, number> = Object.create(null); if (block.state) for (const [n, s] of Object.entries(block.state)) state[n] = s.init;
      const ACTIONS = [null, { action: 'block_entry' }, { action: 'flatten' }, { action: 'allow' }, { action: 'resize_qty' }] as const;
      const out: Array<{ action: string } | null> = [];
      for (let i = 0; i < candles.length; i++) {
        const c = candles[i]; memo = new WeakMap();
        const ctx = { barsSinceEntry: i, entryPrice: candles[0].close, barClose: c.close, barHigh: c.high, barLow: c.low };
        const env: TslEnv = { candle: c, ctx, params, state };
        const code = block.risk ? Math.round(ev(block.risk.action, env)) : 0;
        out.push((ACTIONS[code] ?? null) as { action: string } | null);
        commitState(env);
      }
      return out;
    },
  };
}

/** BlockGateRuntime backed by the interpreter — lets the existing gates validate a TSL block with no
 *  isolate. `code` is ignored (the block is pre-bound); TSL has no host code. */
export function createTslRuntime(block: TslBlock): BlockGateRuntime {
  const itp = createInterpreter(block);
  return {
    runPerBar(_code: string, _bt, candles: Candle[], params: Record<string, unknown>): PerBarOutput | RunError {
      try { return itp.series(candles, params); } catch (e) { return { error: String((e as Error)?.message ?? e) }; }
    },
    runQty(_code: string, ctx: Record<string, unknown>, params: Record<string, unknown>): number | RunError {
      try { return itp.qty(ctx as Record<string, number>, params); } catch (e) { return { error: String((e as Error)?.message ?? e) }; }
    },
    runRiskRuleCheckBar(_code: string, candles: Candle[], params: Record<string, unknown>): Array<{ action: string } | null> | RunError {
      try { return itp.risk(candles, params); } catch (e) { return { error: String((e as Error)?.message ?? e) }; }
    },
  };
}
